import { Box, Stack, TextField, Typography } from "@mui/material";
import React, { useState } from "react";
import ProductDisplay from "./ProductDisplay";
import ButtonTwo from "../../../common/dashboard-button";
import AppChip from "../../../common/app-chip";
import {
  Add,
  Delete,
  Done,
  DragIndicator,
  Edit,
} from "@mui/icons-material";
import { ColorsConstants, grayFontColor } from "../../../cssUtils/constants";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

const SortableProductRow = ({ id, children }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <Box ref={setNodeRef} style={style}>
      {children({ ...attributes, ...listeners })}
    </Box>
  );
};

const SectionDisplay = ({
  section,
  dragHandleProps,
  onRemoveSection,
  onSectionTitleChange,
  onAddProduct,
  onRemoveProduct,
  onChangeProduct,
  onReorderProducts,
  catalogId,
  defaultProductOptions,
  searchProducts,
  selectedRetailerIds,
  disableAddProduct,
}) => {
  const [editTitleMode, setEditTitleMode] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const productIds = (section?.products || []).map((p) => `product-${p.id}`);
  const productCount = section?.products?.length || 0;

  const handleProductDragEnd = (event) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      const oldIndex = productIds.indexOf(active.id);
      const newIndex = productIds.indexOf(over.id);
      onReorderProducts(section?.id, oldIndex, newIndex);
    }
  };

  return (
    <Stack
      gap={2}
      sx={{
        width: "100%",
        p: { xs: 1.5, sm: 2 },
        border: `1px solid ${ColorsConstants.secondaryGray}`,
        borderRadius: 2,
      }}
    >
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        flexWrap="wrap"
        gap={1}
        sx={{ width: "100%" }}
      >
        <Stack direction="row" gap={1.5} alignItems="center" flexWrap="wrap">
          <Box
            {...dragHandleProps}
            sx={{
              display: "flex",
              alignItems: "center",
              color: ColorsConstants.primaryGray40,
              cursor: "grab",
              "&:active": { cursor: "grabbing" },
            }}
          >
            <DragIndicator />
          </Box>
          {editTitleMode ? (
            <Stack direction="row" gap={2} alignItems="center">
              <TextField
                value={section?.title}
                onChange={(e) => {
                  onSectionTitleChange(section?.id, e.target.value);
                }}
                size="small"
              />
              <Done
                sx={{ color: ColorsConstants.primaryBlue90, cursor: "pointer" }}
                onClick={() => setEditTitleMode(false)}
              />
            </Stack>
          ) : (
            <Stack direction="row" gap={2} alignItems="center">
              <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                {section?.title}
              </Typography>
              <Edit
                sx={{ color: ColorsConstants.primaryBlue90, cursor: "pointer" }}
                onClick={() => setEditTitleMode(true)}
              />
            </Stack>
          )}
        </Stack>
        <Stack direction="row" gap={2} alignItems="center">
          <AppChip
            label={`${productCount} product${productCount === 1 ? "" : "s"}`}
            sx={{ backgroundColor: ColorsConstants.primaryBlue60 }}
          />
          <Delete
            sx={{
              color: ColorsConstants.primaryGray40,
              cursor: "pointer",
              fontSize: 22,
            }}
            onClick={() => onRemoveSection(section?.id)}
          />
        </Stack>
      </Stack>

      {productCount > 0 ? (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleProductDragEnd}
        >
          <SortableContext
            items={productIds}
            strategy={verticalListSortingStrategy}
          >
            <Stack gap={2} sx={{ width: "100%", px: 1 }}>
              {section?.products?.map((product) => (
                <SortableProductRow
                  key={product?.id}
                  id={`product-${product.id}`}
                >
                  {(handleProps) => (
                    <ProductDisplay
                      product={product}
                      catalogId={catalogId}
                      defaultOptions={defaultProductOptions}
                      searchProducts={searchProducts}
                      selectedRetailerIds={selectedRetailerIds}
                      dragHandleProps={handleProps}
                      onRemoveProduct={() =>
                        onRemoveProduct(section?.id, product?.id)
                      }
                      onChangeProduct={(newProduct) =>
                        onChangeProduct(section?.id, product?.id, newProduct)
                      }
                    />
                  )}
                </SortableProductRow>
              ))}
            </Stack>
          </SortableContext>
        </DndContext>
      ) : (
        <Typography variant="body2" sx={{ color: grayFontColor, px: 1 }}>
          No products added yet.
        </Typography>
      )}

      <ButtonTwo
        title="Add product"
        startIcon={<Add />}
        onClick={() => onAddProduct(section?.id)}
        disabled={disableAddProduct}
      />
      <Typography
        variant="caption"
        sx={{ color: disableAddProduct ? ColorsConstants.error : grayFontColor }}
      >
        {disableAddProduct
          ? `Product limit reached. Only Meta-approved products can be selected.`
          : "Only Meta-approved products can be selected."}
      </Typography>
    </Stack>
  );
};

export default SectionDisplay;
